/**
 * Publik portal kontenti.
 *
 * Ishlab turgan konturda bu ma'lumotlar CMS'dan keladi
 * (`GET /api/v1/portal/public/` — sozlamalar, bannerlar, xizmat havolalari,
 * yangiliklar, FAQ, to'rt tilda). Bu yerdagi qiymatlar backend ulanmaganda
 * ko'rsatiladigan zaxira (fallback) kontent.
 *
 * Muhim: zaxira kontent ham **tizimning haqiqiy konfiguratsiyasidan** olinadi —
 * `apps/portal/seeds.py` (telefon, e-pochta, xizmat havolalari, FAQ),
 * `apps/catalog/seeds.py` (hodisa turlari), `apps/kuzatuv/seeds.py` (SLA
 * normativlari), `apps/gis/seeds.py` (publik qatlamlar),
 * `apps/integrations/seeds.py` (tashqi tizimlar), `apps/accounts/rbac.py`
 * (rollar). Shuning uchun backend ulanganda ko'rsatkichlar «sakramaydi».
 */
import type { IconName } from "@/shared/ui/Icon";

/* ------------------------------------------------------------- rekvizitlar
   Manba: `apps/portal/seeds.py` → `PortalSettings` (singleton). */

/** Ishonch telefoni — `PortalSettings.hotline_phone`. */
export const HOTLINE = "(71) 207-88-77 (1010)";
/** Rasmiy e-pochta — `PortalSettings.support_email`. */
export const SUPPORT_EMAIL = "info@urmon.uz";
/** Manzil va ish vaqti — kontent-menejer tomonidan CMS'da to'ldiriladi. */
export const ADDRESS = "100043, Toshkent shahri, Chilonzor tumani, Bunyodkor shoh ko'chasi, 7a-uy";
export const WORK_HOURS = "Dushanba–juma, 9:00–18:00";
/** OpenAPI hujjatlari (`config/urls.py`). */
export const API_DOCS_URL = "/api/docs/";

/**
 * Murojaat ID formati — `apps/kuzatuv/services/appeals.py::next_appeal_id`
 * (ЧТЗ 4.2.11.3): `SF-INC-ГГГГ-######`.
 */
export const APPEAL_ID_PATTERN = "SF-INC-2026-000000";
export const APPEAL_ID_SAMPLE = "SF-INC-2026-000842";

export const hero = {
  titleLead: "O'rmon xo'jaligini",
  titleAccent: "raqamli nazorat",
  titleTail: "qilish platformasi",
  lead:
    "Geoportal, sun'iy yo'ldosh monitoringi, ijara va o'rmon tiklash hisobi, elektron " +
    "xizmatlar hamda prokuratura nazorati — 13 ta kichik tizim yagona ma'lumot manbai " +
    "asosida ishlaydi.",
  primary: "Kabinetga kirish",
  secondary: "Bog'lanish",
  note: "Murojaat yuborish, treking va ochiq xarita avtorizatsiyasiz ishlaydi",
};

/**
 * Hero ko'rsatkichlari — platformaning tuzilmaviy hajmi. Bular ma'lumot
 * bazasidagi operativ raqamlar emas, shuning uchun backendsiz ham to'g'ri
 * turadi (13 kichik tizim, 22 integratsiya, 6 ochiq qatlam, 14 hudud).
 */
export const heroStats = [
  { label: "Kichik tizimlar", value: "13", unit: "" },
  { label: "Integratsiya qilingan tashqi tizim", value: "22", unit: "" },
  { label: "Ochiq xarita qatlamlari", value: "6", unit: "" },
  { label: "Hududiy qamrov", value: "14", unit: "hudud" },
];

export interface Subsystem {
  num: string;
  icon: IconName;
  title: string;
  text: string;
}

/** ЧТЗ 4.2 — 13 ta kichik tizim (README §2 jadvali bilan bir xil tartibda). */
export const subsystems: Subsystem[] = [
  {
    num: "01",
    icon: "map",
    title: "Geoma'lumot va kartografiya",
    text: "Qatlamlar, kvartal va uchastka chegaralari, versiyalash, import/eksport, PDF chop etish.",
  },
  {
    num: "02",
    icon: "database",
    title: "Ma'lumotlarni boshqarish va integratsiya",
    text: "To'rt tilli ma'lumotnomalar, klassifikatorlar va 22 ta tashqi tizim bilan almashinuv.",
  },
  {
    num: "03",
    icon: "activity",
    title: "Monitoring va hodisalarni boshqarish",
    text: "Hodisalar, IoT qurilmalar va sensor o'qishlari — aniqlashdan yopilishgacha bo'lgan sikl.",
  },
  {
    num: "04",
    icon: "briefcase",
    title: "Ijara yerlarini boshqarish",
    text: "Uchastka hayot sikli: auksion, ariza, shartnoma va majburiyatlar bajarilishi nazorati.",
  },
  {
    num: "05",
    icon: "tree",
    title: "O'rmon tiklash va yerga joylashtirish hisobi",
    text: "Obyektlar, ko'chat, tirik qolish darajasi va «Yashil makon» bilan kelishuv.",
  },
  {
    num: "06",
    icon: "package",
    title: "Mahsulot va resurslar hisobi",
    text: "Katalog, kirim-chiqim, inventarizatsiya va omborlar bo'yicha qoldiqlar.",
  },
  {
    num: "07",
    icon: "shield",
    title: "Administratsiya va xavfsizlik",
    text: "15 ta rol matritsasi, MFA, sessiyalar va o'zgarmas audit jurnali.",
  },
  {
    num: "08",
    icon: "camera",
    title: "Monitoring va kuzatuv (Kuzatuv)",
    text: "Operator dashboardi, AI-pitomnik, fuqaro murojaatlari va SLA taymerlari.",
  },
  {
    num: "09",
    icon: "file",
    title: "Elektron xizmatlar",
    text: "Billing-urmon: shartnoma, invoys, to'lov, penya, ortiqcha to'lov va qaytarish.",
  },
  {
    num: "10",
    icon: "building",
    title: "Korporativ kabinet",
    text: "Ichki xabarlar, videokonferensiya, kadrlar hisobi (PINFL) va topshiriqlar nazorati.",
  },
  {
    num: "11",
    icon: "globe",
    title: "Publik portal",
    text: "CMS, yangiliklar, ochiq ma'lumotlar, murojaat qabuli va uning trekingi.",
  },
  {
    num: "12",
    icon: "smartphone",
    title: "Mobil veb-versiya",
    text: "900 px dan kichik ekranlarda menyu, xarita paneli va bosish maydonlari moslashadi.",
  },
  {
    num: "13",
    icon: "scale",
    title: "Raqamli nazorat",
    text: "Prokuratura uchun faqat o'qish kirishi: tekshiruv, dalillar va tahliliy kesim.",
  },
];

/**
 * SLA normativlari — `apps/kuzatuv/seeds.py::SLA_RULES`, «yong'in / kritik»
 * qatori: operator 5 daqiqada javob beradi, moderator 1 soatda qabul qiladi,
 * murojaat 12 soatda yakunlanadi.
 */
export interface ServiceItem {
  icon: IconName;
  title: string;
  text: string;
  tags: string[];
}

/** Fuqarolar va biznes uchun xizmatlar (ЧТЗ 4.2.11.3, 4.2.9, 4.2.10). */
export const services: ServiceItem[] = [
  {
    icon: "message",
    title: "Murojaat yuborish",
    text:
      "Yong'in, noqonuniy kesish yoki axlat haqida xabar bering — nuqta xaritada " +
      "belgilanadi, foto biriktiriladi.",
    tags: ["Avtorizatsiyasiz", "ID SMS orqali"],
  },
  {
    icon: "target",
    title: "Murojaatni kuzatish",
    text:
      "Murojaat ID va telefon raqamingizning oxirgi 4 raqami bo'yicha bosqichlar va " +
      "natijani ko'ring.",
    tags: ["ID + 4 raqam", "yoki SMS-kod"],
  },
  {
    icon: "briefcase",
    title: "Ijara va auksion uchastkalari",
    text: "Bo'sh va auksionga qo'yilgan uchastkalarni xaritadan tanlab, onlayn ariza yuboring.",
    tags: ["OneID", "Ўзкадастр"],
  },
  {
    icon: "file",
    title: "To'lov va invoyslar",
    text: "Shartnoma bo'yicha hisob-kitob, penya, ortiqcha to'lov va uni qaytarish.",
    tags: ["Payme", "Click", "MUNIS"],
  },
  {
    icon: "store",
    title: "«Yashil olam» savdo maydonchasi",
    text: "Ko'chatxona va o'rmon xo'jaliklari mahsulotini onlayn xarid qilish.",
    tags: ["Elektron shartnoma", "OneID"],
  },
  {
    icon: "download",
    title: "Ochiq ma'lumotlar va API",
    text:
      "Ochiq qatlamlar va statistikani CSV, XLSX yoki GeoJSON'da yuklang, dasturiy " +
      "ulanish uchun OpenAPI hujjatlaridan foydalaning.",
    tags: ["/api/docs/", "data.gov.uz"],
  },
];

export interface AppealType {
  code: string;
  color: string;
  title: string;
  /** Operator javobining SLA normativi (`apps/kuzatuv/seeds.py::SLA_RULES`). */
  sla: string;
}

/**
 * Publik murojaat formasida ko'rinadigan hodisa turlari — `EventType` da
 * `appeal_visible=True` (`apps/catalog/seeds.py::EVENT_TYPES`).
 * Backend ulanganda `GET /api/v1/portal/public/appeal-types/` dan keladi.
 */
export const appealTypes: AppealType[] = [
  { code: "fire", color: "#D32F2F", title: "Yong'in", sla: "5 daqiqa" },
  { code: "smoke", color: "#F57C00", title: "Tutun aniqlandi", sla: "10 daqiqa" },
  { code: "illegal_logging", color: "#7B1FA2", title: "Noqonuniy kesish", sla: "15 daqiqa" },
  { code: "dump", color: "#5D4037", title: "Axlat tashlash", sla: "60 daqiqa" },
  {
    code: "illegal_construction",
    color: "#455A64",
    title: "Noqonuniy qurilish",
    sla: "30 daqiqa",
  },
  { code: "other", color: "#9E9E9E", title: "Boshqa", sla: "120 daqiqa" },
];

export interface TrackingStep {
  /** `Appeal.STATUS` kodi (`apps/kuzatuv/models.py`). */
  status: string;
  title: string;
  text: string;
}

/** Murojaat oqimi: new → in_review → routed → in_progress → result_entered → closed. */
export const trackingSteps: TrackingStep[] = [
  {
    status: "new",
    title: "Yangi",
    text: "Murojaat ro'yxatga olindi, ID SMS orqali yuborildi.",
  },
  {
    status: "in_review",
    title: "Ko'rib chiqilmoqda",
    text: "Navbatchi operator xabarni tekshiradi va xavf darajasini belgilaydi.",
  },
  {
    status: "routed",
    title: "Yo'naltirildi",
    text: "Hudud va toifa bo'yicha mas'ul tashkilotga biriktiriladi.",
  },
  {
    status: "in_progress",
    title: "Ijroda",
    text: "Joyida chora ko'riladi, dalillar va hisobot yuklanadi.",
  },
  {
    status: "result_entered",
    title: "Natija kiritildi",
    text: "Natijani bo'lim rahbari tasdiqlaydi.",
  },
  {
    status: "closed",
    title: "Yakunlandi",
    text: "SMS orqali xabar beriladi, baholash so'raladi.",
  },
];

/** Holat kodi → yorliq (treking natijasini ko'rsatishda ishlatiladi). */
export const appealStatusLabels: Record<string, string> = Object.fromEntries(
  trackingSteps.map((step) => [step.status, step.title]),
);

export interface PublicLayer {
  /** Backend qatlam kodi — ro'yxat kaliti (interfeysda ko'rsatilmaydi). */
  code: string;
  title: string;
  /** Proksi orqali chiqariladigan atributlar (`apps/gis/seeds.py::LAYERS`). */
  attributes: string;
}

/**
 * ЧТЗ 4.2.11.2 — ochiq xaritada `visibility="public"` qatlamlar ko'rinadi.
 * Qolganlari (hodisalar, IoT qurilmalar, kvartallar, o'rmon tiklash
 * obyektlari) faqat kabinetda ko'rinadi.
 */
export const publicLayers: PublicLayer[] = [
  {
    code: "forest_fund",
    title: "O'rmon fondi yerlari",
    attributes: "nomi, maydon (ga), o'rmon xo'jaligi",
  },
  { code: "nurseries", title: "Ko'chatxonalar", attributes: "nomi, aloqa ma'lumoti" },
  { code: "fire_risk", title: "Yong'in xavfi", attributes: "holat, aniqlangan vaqt, jiddiylik" },
  { code: "lease_plots", title: "Ijara hududlari", attributes: "maydon (ga), holat" },
  { code: "auction_plots", title: "Auksion yerlari", attributes: "maydon (ga), auksion raqami" },
  {
    code: "vegetation_index",
    title: "Vegetativ indeks (NDVI)",
    attributes: "ndvi qiymati, kuzatuv sanasi",
  },
];

export interface FaqEntry {
  category: string;
  question: string;
  answer: string;
}

/** ЧТЗ 4.2.11.1 — savol-javob bloki. Manba: `apps/portal/seeds.py::FAQ`. */
export const faq: FaqEntry[] = [
  {
    category: "Umumiy",
    question: "Murojaat yuborish uchun ro'yxatdan o'tish kerakmi?",
    answer:
      "Yo'q. Murojaat avtorizatsiyasiz yuboriladi — telefon raqami va xaritada joyni " +
      "ko'rsatish yetarli. Ro'yxatdan o'tish faqat shartnoma va to'lov bilan bog'liq " +
      "xizmatlarda talab qilinadi.",
  },
  {
    category: "Umumiy",
    question: "Murojaatim holatini qanday bilaman?",
    answer:
      `SMS orqali kelgan ID (masalan ${APPEAL_ID_SAMPLE}) va telefon raqamingizning ` +
      "oxirgi 4 raqami bilan «Murojaatingiz qayerda?» bo'limidan tekshiring. Telefon " +
      "yoningizda bo'lmasa — SMS-kod so'rash mumkin.",
  },
  {
    category: "Ijara",
    question: "Ijara to'lovini onlayn to'lash mumkinmi?",
    answer:
      "Ha. Shaxsiy kabinetdagi invoysni Payme, Click yoki MUNIS orqali to'lash mumkin. " +
      "To'lov tarixi, penya va ortiqcha to'lovni qaytarish arizasi ham shu yerda.",
  },
  {
    category: "Yashil olam",
    question: "«Yashil olam» da ko'chat qanday buyurtma qilinadi?",
    answer:
      "Katalogdan mahsulotni tanlang, savatga qo'shing va OneID orqali kirib buyurtmani " +
      "rasmiylashtiring. Shartnoma elektron shaklda tuziladi.",
  },
  {
    category: "Ochiq ma'lumotlar",
    question: "Ochiq ma'lumotlarni qanday yuklab olaman?",
    answer:
      "Ochiq qatlamlar va statistika CSV, XLSX hamda GeoJSON formatlarida yuklab " +
      `olinadi. Dasturiy ulanish uchun OpenAPI hujjatlari ochiq (${API_DOCS_URL}), ` +
      "ma'lumotlar data.gov.uz portalida ham e'lon qilinadi.",
  },
  {
    category: "Xavfsizlik",
    question: "Shaxsiy ma'lumotlarim qanday himoyalanadi?",
    answer:
      "Telefon raqami xesh ko'rinishida saqlanadi, trekingda xodimlarning shaxsiy " +
      "ma'lumotlari oshkor qilinmaydi, ochiq xarita atributlari proksi orqali " +
      "filtrlanadi. Barcha amallar o'zgarmas audit jurnaliga yoziladi.",
  },
];

export interface GuideItem {
  icon: IconName;
  audience: string;
  title: string;
  text: string;
  format: string;
}

/** ЧТЗ 4.2.11.1 — qo'llanmalar (`portal.GuideDocument`, `audience` bo'yicha). */
export const guides: GuideItem[] = [
  {
    icon: "user",
    audience: "individual",
    title: "Fuqarolar uchun qo'llanma",
    text: "Murojaat yuborish, treking va ochiq xarita bilan ishlash.",
    format: "PDF",
  },
  {
    icon: "briefcase",
    audience: "legal_entity",
    title: "Yuridik shaxslar uchun qo'llanma",
    text: "Ijara arizasi, shartnoma, invoys va «Yashil olam» savdosi.",
    format: "PDF",
  },
  {
    icon: "users",
    audience: "employee",
    title: "Xodimlar uchun video-yo'riqnoma",
    text: "Kabinet, hodisalar bilan ishlash va SLA taymerlari.",
    format: "Video",
  },
];

export interface ContactItem {
  icon: IconName;
  label: string;
  value: string;
  note: string;
  href?: string;
}

/**
 * «Bog'lanish» bo'limi — futerdagi rasmiy rekvizitlar bilan bir xil manba.
 * Qiymatlar CMS'dan keladi (`usePortalConfig`), backend ulanmaganda esa
 * yuqoridagi konstantalar ishlatiladi.
 */
export function makeContacts(config: {
  hotline: string;
  email: string;
  address: string;
  workHours: string;
}): ContactItem[] {
  return [
    {
      icon: "phone",
      label: "Ishonch telefoni",
      value: config.hotline,
      note: "24/7, qo'ng'iroq bepul",
      href: `tel:${config.hotline}`,
    },
    {
      icon: "mail",
      label: "Rasmiy e-pochta",
      value: config.email,
      note: "Murojaatlar 3 ish kunida ko'rib chiqiladi",
      href: `mailto:${config.email}`,
    },
    {
      icon: "map",
      label: "Manzil",
      value: config.address,
      note: config.workHours,
    },
  ];
}

/** Zaxira rekvizitlar (backend ulanmagan holat). */
export const contacts: ContactItem[] = makeContacts({
  hotline: HOTLINE,
  email: SUPPORT_EMAIL,
  address: ADDRESS,
  workHours: WORK_HOURS,
});

/**
 * AI yordamchi oynasi uchun matnlar va zaxira javoblar.
 *
 * Ishlab turgan konturda savol backendga (`POST /api/v1/assistant/ask/`)
 * yuboriladi. Backend ulanmaganda quyidagi kalit so'z jadvali ishlaydi:
 * tashqi model chaqirilmaydi, ma'lumot brauzerdan chiqmaydi.
 */
export const assistant = {
  title: "Smart Forestry AI",
  status: "Onlayn — o'rtacha javob 5 soniya",
  greeting:
    "Assalomu alaykum! Men platformaning AI yordamchisiman. Xizmatlar, murojaat " +
    "holati, ijara va ochiq ma'lumotlar bo'yicha savol bering.",
  placeholder: "Savolingizni yozing…",
  disclaimer: "Javoblar ma'lumot uchun. Rasmiy javob murojaat orqali beriladi.",
  suggestions: [
    "Murojaatimni qanday kuzataman?",
    "Ijaraga uchastka qanday olinadi?",
    "Yong'in haqida qayerga xabar beraman?",
    "Ochiq ma'lumotlarni qanday yuklayman?",
  ],
  replies: [
    {
      keys: ["murojaat", "treking", "kuzat", "ariza raqam", "holat", "id"],
      text:
        `Murojaat yuborilgach SMS orqali ${APPEAL_ID_PATTERN} ko'rinishidagi ID keladi. ` +
        "Uni bosh sahifadagi «Murojaatingiz qayerda?» bo'limiga telefon raqamingizning " +
        "oxirgi 4 raqami bilan birga kiriting — bosqichlar va natija ko'rinadi. " +
        "Avtorizatsiya talab qilinmaydi.",
    },
    {
      keys: ["ijara", "uchastka", "shartnoma", "yer", "auksion"],
      text:
        "Bo'sh va auksionga qo'yilgan uchastkalarni ochiq xaritadan tanlaysiz va " +
        "onlayn ariza yuborasiz. Shartnoma va to'lov bilan bog'liq amallar OneID " +
        "orqali tasdiqlangan shaxsni talab qiladi. Hisob-kitob, penya va to'lov " +
        "tarixi kabinetda ko'rinadi.",
    },
    {
      keys: ["yong'in", "yongin", "kesish", "qonunbuzarlik", "hodisa", "xabar ber"],
      text:
        "Yong'in yoki qonunbuzarlik haqida «Murojaat yuborish» xizmati orqali xabar " +
        "bering — joylashuv va foto biriktiriladi, xabar navbatchi operator markaziga " +
        "tushadi. Yong'in signaliga operator 5 daqiqada javob beradi. Shoshilinch " +
        `holatda ishonch telefoni: ${HOTLINE} (24/7, bepul).`,
    },
    {
      keys: ["ochiq ma'lumot", "ochiq malumot", "csv", "xlsx", "geojson", "api", "yuklab"],
      text:
        "Statistika va geoqatlamlar CSV, XLSX va GeoJSON formatlarida yuklab olinadi. " +
        `Dasturiy ulanish uchun API hujjatlari ochiq (${API_DOCS_URL}), ma'lumotlar ` +
        "data.gov.uz portalida ham e'lon qilinadi.",
    },
    {
      keys: ["kirish", "kabinet", "oneid", "parol", "login"],
      text:
        "Kabinetga xizmat e-pochtangiz yoki OneID orqali kirasiz. Huquqlaringizga mos " +
        "ish joyi 15 ta roldan iborat matritsa asosida avtomatik shakllanadi, kirish " +
        "urinishlari audit jurnaliga yoziladi. Parolni unutgan bo'lsangiz — kirish " +
        "sahifasidagi «Parolni unutdingizmi?» havolasi.",
    },
    {
      keys: ["xarita", "geoportal", "qatlam"],
      text:
        "Ochiq geoportalda 6 ta qatlam mavjud: o'rmon fondi yerlari, ko'chatxonalar, " +
        "yong'in xavfi, ijara hududlari, auksion yerlari va vegetativ indeks (NDVI). " +
        "Shaxsiy va tijorat atributlari proksi orqali filtrlanadi.",
    },
    {
      keys: ["to'lov", "tolov", "invoys", "penya", "payme", "click", "munis"],
      text:
        "Invoyslar Payme, Click yoki MUNIS orqali to'lanadi. Penya avtomatik " +
        "hisoblanadi, ortiqcha to'lovni qaytarish arizasi ham kabinet orqali " +
        "topshiriladi.",
    },
    {
      keys: ["bog'lan", "boglan", "telefon", "manzil", "aloqa"],
      text:
        `Ishonch telefoni: ${HOTLINE} (24/7, bepul). E-pochta: ${SUPPORT_EMAIL}. ` +
        `Manzil: ${ADDRESS}, ${WORK_HOURS.toLowerCase()}.`,
    },
  ],
  fallback:
    "Bu savol bo'yicha aniq ma'lumotim yo'q. «Murojaat yuborish» xizmati orqali " +
    `savolingizni rasmiy tarzda yuboring yoki ishonch telefoniga qo'ng'iroq qiling: ${HOTLINE}.`,
};

export const news = [
  {
    tag: "Monitoring",
    date: "2026-08-04",
    title: "Sun'iy yo'ldosh tahlili yangi hududlarga kengaytirildi",
    text: "Qoraqalpog'iston va Buxoro bo'yicha o'zgarishlarni avtomatik aniqlash yoqildi.",
  },
  {
    tag: "Xizmatlar",
    date: "2026-07-28",
    title: "Ijara arizasi endi to'liq onlayn topshiriladi",
    text: "Qog'oz hujjatlar bekor qilindi, imzo OneID orqali elektron tarzda qo'yiladi.",
  },
  {
    tag: "Yashil makon",
    date: "2026-07-15",
    title: "Mavsumiy ekish natijalari platformada e'lon qilindi",
    text: "Tirik qolish darajasi hududlar kesimida ochiq ma'lumot sifatida joylandi.",
  },
];
